import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { makeLabeler, useCommonCodes } from '../lib/codes'
import { adminApi, type MatchUnmetItem } from '../lib/adminApi'

/**
 * 미충족 큐(개편안 R-9) — **최신 실행에 등급 «높음» 후보가 없는 공개 문제카드** 목록.
 *
 * 시민 화면에는 높음(과 이후 조합)만 나가므로, 여기 있는 문제는 시민에게 내보낼 것이
 * 없는 상태다. 큐는 목록만 만들고 **액션은 결과 화면에서 한다** — 협의중 전이(액션 ①)는
 * 후보 행의 상태 버튼, 해결카드 전환(액션 ④)은 카드 등록이 맡는다. 조합 제안·합작
 * 카드(액션 ②·③)는 후속 단계다.
 */

const UNMET_REASON = 'unmet_reason'

export function AdminMatchUnmetPage() {
  const queue = useQuery({
    queryKey: ['admin-match-unmet'],
    queryFn: () => adminApi.matchUnmet(100),
  })
  const reasonCodes = useCommonCodes(UNMET_REASON)
  const reasonLabel = makeLabeler(reasonCodes.data)
  const items: MatchUnmetItem[] = queue.data?.items ?? []

  return (
    <div>
      <Link className="back-link" to="/admin/match">
        ← 매칭 목록
      </Link>

      <div className="admin-bar">
        <div>
          <h1 className="page-title">미충족 큐</h1>
          <p className="page-desc">
            최신 매칭에서 <b>등급 «높음» 후보가 없는</b> 공개 문제입니다. 시민 화면에는 높음만
            나가므로, 여기 있는 문제는 관리자의 다음 행동(후보 기업 협의 · 해결카드 전환)이
            필요합니다.
          </p>
        </div>
      </div>

      <QueryState
        isPending={queue.isPending}
        error={queue.error}
        isEmpty={items.length === 0}
        emptyMessage="미충족 문제가 없습니다 — 공개된 문제 전부에 «높음» 후보가 있습니다."
      >
        <table className="admin-table unmet-table">
          <thead>
            <tr>
              <th>문제</th>
              <th>후보 · 제외</th>
              <th>미충족 사유</th>
              <th>진행</th>
              <th>최근 실행</th>
              <th>매칭</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.problemCard.id}>
                <td>
                  <b>{i.problemCard.title}</b>
                </td>
                <td>
                  {i.noMatch ? (
                    <span className="badge">전원 제외</span>
                  ) : (
                    <>후보 {i.candidateCount} · 제외 {i.excludedCount}</>
                  )}
                </td>
                <td>
                  {/* 사유는 전원 제외(no_match) 실행에만 기록된다 — 후보가 있으면 빈 칸이 정상이다. */}
                  {i.unmetReasonCodes.length === 0 ? (
                    <span className="muted small">—</span>
                  ) : (
                    i.unmetReasonCodes.map((code) => (
                      <span key={code} className="badge">
                        {reasonLabel(code)}
                      </span>
                    ))
                  )}
                </td>
                <td>
                  {i.negotiatingCount > 0 && (
                    <span className="badge">협의중 {i.negotiatingCount}</span>
                  )}
                  {i.convertedSolutionCardId !== null && (
                    <Link className="badge" to={`/admin/cards/${i.convertedSolutionCardId}`}>
                      해결카드 전환됨
                    </Link>
                  )}
                  {i.negotiatingCount === 0 && i.convertedSolutionCardId === null && (
                    <span className="muted small">미착수</span>
                  )}
                </td>
                <td className="muted small">{new Date(i.executedAt).toLocaleString('ko-KR')}</td>
                <td>
                  <Link className="btn ghost small-btn" to={`/admin/match/${i.problemCard.id}`}>
                    결과 보기
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </div>
  )
}
